import { Tables } from "@/lib/supabase/supabaseTypesImport";
import {
	MessageType,
	JobRequestCustomV1,
} from "@/lib/store/Realtime/message.types";
import { MessageContent } from "@/lib/store/Realtime/message.types";
import Button from "@mui/material/Button";
import { useState, useEffect } from "react";
import "./styles.css";
import Tooltip from "@mui/material/Tooltip";
import { FileDown } from "lucide-react";

interface MessageActionsProps {
	message: Tables<"messages_with_actions">;
	onAcceptJobRequest?: (
		messageContent?: JobRequestCustomV1 | null
	) => Promise<boolean>;
	onDenyJobRequest?: () => Promise<boolean>;
	onExportFormPdf?: () => Promise<void>;
	currentOrganizationId: string;
	otherOrganizationId: string;
}

/**
 * Checks if the actions are visible for the current organization
 * @param message
 * @param visibleForOrg Should be true if the message is visible for the current organization
 * @param actions Array of actions that shouldn't be already triggered
 * @returns
 */
const isActionVisible = (
	message: Tables<"messages_with_actions">,
	visibleForOrg: boolean,
	actions: string[]
) => {
	return (
		visibleForOrg &&
		(!message.actions ||
			(typeof message.actions === "object" &&
				!actions.some(
					(action) =>
						action in
							(message.actions as Record<string, unknown>) ||
						(message.actions as Record<string, unknown>)[action] !==
							undefined
				)))
	);
};

const MessageActions: React.FC<MessageActionsProps> = ({
	message,
	onAcceptJobRequest,
	onDenyJobRequest,
	onExportFormPdf,
	currentOrganizationId,
	otherOrganizationId,
}) => {
	const [isLeaving, setIsLeaving] = useState(false);
	const [content, setContent] = useState<React.ReactNode | null>(null);

	const messageOfCurrentOrg = currentOrganizationId === otherOrganizationId;

	const handleAcceptJobRequest = async () => {
		if (onAcceptJobRequest && message.id) {
			const contentToPass =
				messageContent?.type === MessageType.JOB_REQUEST_CUSTOM_V1
					? (messageContent as JobRequestCustomV1)
					: null;

			await onAcceptJobRequest(contentToPass);
		}
	};

	const handleDenyJobRequest = async () => {
		if (onDenyJobRequest && message.id) {
			await onDenyJobRequest();
		}
	};

	let messageContent: MessageContent | null = null;
	try {
		messageContent = message?.content as MessageContent;

		if (messageContent && typeof messageContent === "string") {
			messageContent = JSON.parse(messageContent) as MessageContent;
		}
	} catch (e) {
		messageContent = null;
	}

	const renderMessageContent = () => {
		switch (messageContent?.type) {
			case MessageType.JOB_REQUEST_V1:
			case MessageType.JOB_REQUEST_CUSTOM_V1:
				const showAcceptDeny =
					onAcceptJobRequest &&
					onDenyJobRequest &&
					isActionVisible(message, !messageOfCurrentOrg, [
						"job_request_accepted",
						"job_request_denied",
					]);

				if (showAcceptDeny) {
					return (
						<JobRequestActions
							onAcceptJobRequest={handleAcceptJobRequest}
							onDenyJobRequest={handleDenyJobRequest}
						/>
					);
				}
				return null;
			default:
				return null;
		}
	};

	useEffect(() => {
		const newContent = renderMessageContent();
		if (!newContent && content) {
			// Content is about to be removed, start leaving animation
			setIsLeaving(true);
			const timer = setTimeout(() => {
				setContent(null);
				setIsLeaving(false);
			}, 300); // Match animation duration
			return () => clearTimeout(timer);
		} else {
			setContent(newContent);
		}
	}, [message, messageOfCurrentOrg]);

	return (
		<div
			className={`message-transition ${
				isLeaving ? "leaving" : ""
			} flex flex-wrap gap-2`}
		>
			{content}
		</div>
	);
};

const JobRequestActions = ({
	onAcceptJobRequest,
	onDenyJobRequest,
}: {
	onAcceptJobRequest: () => Promise<void>;
	onDenyJobRequest: () => Promise<void>;
}) => {
	return (
		<AcceptAndDenyActions
			onAccept={onAcceptJobRequest}
			onDeny={onDenyJobRequest}
			textAccept="Akzeptieren"
			textDeny="Ablehnen"
			testIdAccept="job-request-accept-button"
			testIdDeny="job-request-deny-button"
		/>
	);
};

const AcceptAndDenyActions = ({
	onAccept,
	onDeny,
	textAccept,
	textDeny,
	disabled,
	tooltipText,
	testIdAccept,
	testIdDeny,
}: {
	onAccept: () => void;
	onDeny: () => void;
	textAccept: string;
	textDeny: string;
	disabled?: boolean;
	tooltipText?: string;
	testIdAccept?: string;
	testIdDeny?: string;
}) => {
	return (
		<Tooltip title={tooltipText}>
			<div>
				<ActionButton
					text={textAccept}
					onClick={onAccept}
					disabled={disabled}
					color="success"
					testId={testIdAccept}
				/>
				<ActionButton
					text={textDeny}
					onClick={onDeny}
					disabled={disabled}
					color="error"
					testId={testIdDeny}
				/>
			</div>
		</Tooltip>
	);
};

const ActionButton = ({
	text,
	onClick,
	disabled,
	color,
	testId,
}: {
	text: string;
	onClick: () => void;
	disabled?: boolean;
	color?: "success" | "error";
	testId?: string;
}) => {
	return (
		<Button
			size="small"
			variant="outlined"
			color={color}
			onClick={onClick}
			className="button-transition"
			sx={{
				fontSize: "0.75rem",
				py: 0.5,
				textTransform: "none",
				height: "30px",
				margin: "2px 2px",
			}}
			disabled={disabled}
			data-testid={testId}
			aria-label={text}
		>
			{text}
		</Button>
	);
};

const ExportPdfAction = ({ onExport }: { onExport: () => Promise<void> }) => {
	const [isExporting, setIsExporting] = useState(false);

	const handleExport = async () => {
		setIsExporting(true);
		try {
			await onExport();
		} finally {
			setIsExporting(false);
		}
	};

	return (
		<Button
			size="small"
			variant="outlined"
			onClick={handleExport}
			disabled={isExporting}
			className="button-transition"
			sx={{
				fontSize: "0.75rem",
				py: 0.5,
				textTransform: "none",
				height: "30px",
				margin: "2px 2px",
			}}
			startIcon={<FileDown size={14} />}
		>
			{isExporting ? "Exportiere..." : "Formular als PDF"}
		</Button>
	);
};

export default MessageActions;
